import { appAlert } from '@/lib/appAlert';
import { getErrorMessage } from '@/lib/functions';
import { useTranslation } from 'react-i18next';
﻿import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useProfile } from '@/contexts/ProfileContext';
import {
  fetchIAPProducts,
  IAP_PRODUCTS,
  purchaseProduct,
  verifyAndCredit,
  type IAPProduct,
  type IAPProductId,
} from '@/lib/iap';

export default function WalletTopupScreen() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { refresh } = useProfile();

  const [selected, setSelected] = useState<IAPProductId>('credits_10');
  const [products, setProducts] = useState<IAPProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchIAPProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoadingProducts(false));
  }, []);

  function getPrice(productId: string): string {
    const p = products.find((x) => x.id === productId);
    return p?.displayPrice ?? '…';
  }

  async function handlePurchase() {
    setPurchasing(true);
    setError(null);
    try {
      const purchase = await purchaseProduct(selected);
      const credits = await verifyAndCredit(purchase);
      await refresh();
      appAlert(
        t('wallet.topup_purchase_title'),
        t('wallet.topup_purchase_body', { count: credits }),
        [{ text: t('common.ok'), onPress: () => router.back() }],
      );
    } catch (e) {
      setError(getErrorMessage(e, t('wallet.purchase_failed')));
    } finally {
      setPurchasing(false);
    }
  }

  const selectedMeta = IAP_PRODUCTS.find((p) => p.id === selected)!;

  if (Platform.OS === 'web') {
    return (
      <View style={[styles.container, { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl, justifyContent: 'center', alignItems: 'center' }]}>
        <Ionicons name="logo-google-playstore" size={48} color={colors.textMuted} />
        <Text style={[styles.title, { marginTop: spacing.lg, textAlign: 'center' }]}>
          {t('wallet.web_title')}
        </Text>
        <Text style={[styles.subtitle, { marginTop: spacing.md }]}>
          {t('wallet.web_subtitle')}
        </Text>
        <Pressable onPress={() => router.back()} style={{ marginTop: spacing.xl }} hitSlop={12}>
          <Text style={{ color: colors.primaryLight, fontSize: fontSize.md, fontWeight: '600' }}>{t('common.back')}</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.xl }]}>
      <View style={styles.topBar}>
        <Pressable onPress={() => router.back()} hitSlop={12} accessibilityRole="button">
          <Text style={styles.back}>{t('common.back')}</Text>
        </Pressable>
        <Text style={styles.title}>{t('wallet.topup_screen_title')}</Text>
        <View style={styles.spacer} />
      </View>

      <Text style={styles.subtitle}>
        {t('wallet.topup_subtitle_detail')}
      </Text>

      {loadingProducts ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator color={colors.primary} />
          <Text style={styles.loadingText}>{t('wallet.topup_loadingPrices')}</Text>
        </View>
      ) : (
        <View style={styles.amounts}>
          {IAP_PRODUCTS.map((item) => {
            const isSelected = selected === item.id;
            return (
              <Pressable
                key={item.id}
                style={[styles.amountCard, isSelected && styles.amountCardSelected]}
                onPress={() => setSelected(item.id)}
                accessibilityRole="radio"
                accessibilityState={{ checked: isSelected }}
              >
                <Text style={[styles.amountValue, isSelected && styles.amountValueSelected]}>
                  {item.credits}
                </Text>
                <Text style={[styles.amountUnit, isSelected && styles.amountUnitSelected]}>
                  {t('wallet.topup_unit')}
                </Text>
                <Text style={[styles.amountPrice, isSelected && styles.amountPriceSelected]}>
                  {getPrice(item.id)}
                </Text>
                {isSelected && (
                  <View style={styles.checkWrap}>
                    <Ionicons name="checkmark-circle" size={18} color={colors.primary} />
                  </View>
                )}
              </Pressable>
            );
          })}
        </View>
      )}

      <View style={styles.infoCard}>
        <Ionicons name="storefront-outline" size={16} color={colors.textMuted} />
        <Text style={styles.infoText}>
          {t('wallet.topup_infoText')}
        </Text>
      </View>

      {!!error && (
        <View style={styles.errorCard}>
          <Ionicons name="alert-circle-outline" size={16} color={colors.danger} />
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      <Button
        title={t('wallet.topup_buyButton', { count: selectedMeta.credits })}
        icon="color-fill-outline"
        loading={purchasing}
        disabled={loadingProducts}
        onPress={handlePurchase}
      />
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
      paddingHorizontal: spacing.lg,
      gap: spacing.lg,
    },
    topBar: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    back: {
      color: colors.primaryLight,
      fontSize: fontSize.md,
      fontWeight: '600',
      width: 70,
    },
    title: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    spacer: { width: 70 },
    subtitle: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      lineHeight: 22,
      textAlign: 'center',
    },
    loadingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.sm,
      paddingVertical: spacing.xl,
    },
    loadingText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
    },
    amounts: {
      flexDirection: 'row',
      gap: spacing.md,
    },
    amountCard: {
      flex: 1,
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 2,
      borderColor: colors.border,
      padding: spacing.md,
      alignItems: 'center',
      gap: 2,
      ...shadow.card,
    },
    amountCardSelected: {
      borderColor: colors.primary,
      backgroundColor: `${colors.primary}10`,
    },
    amountValue: {
      color: colors.text,
      fontSize: fontSize.xxl,
      fontWeight: '800',
    },
    amountValueSelected: {
      color: colors.primary,
    },
    amountUnit: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
    },
    amountUnitSelected: {
      color: colors.primary,
    },
    amountPrice: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      fontWeight: '700',
      marginTop: spacing.xs,
    },
    amountPriceSelected: {
      color: colors.primary,
    },
    checkWrap: {
      position: 'absolute',
      top: 6,
      right: 6,
    },
    infoCard: {
      flexDirection: 'row',
      gap: spacing.sm,
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      alignItems: 'flex-start',
    },
    infoText: {
      flex: 1,
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 19,
    },
    errorCard: {
      flexDirection: 'row',
      gap: spacing.sm,
      backgroundColor: `${colors.danger}10`,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: `${colors.danger}30`,
      padding: spacing.md,
      alignItems: 'flex-start',
    },
    errorText: {
      flex: 1,
      color: colors.danger,
      fontSize: fontSize.sm,
      lineHeight: 19,
    },
  });
}
